import { useTranslation } from "react-i18next";
import { useAxiosContext } from "../..";
import { FormikErrors, useFormik } from "formik";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FieldErrorMessage, FormLabel } from "../../utils/formValidator";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import i18n from "../../utils/i18n";
import { InputTextarea } from "primereact/inputtextarea";
import applicationAuditPlanService from "../../services/application-audit-plans/application-audit-plans.service";
import {
  ApplicationUnitDropdownDto,
  CreateApplicationAuditPlanDto,
  UpdateApplicationAuditPlanDto,
} from "../../services/application-audit-plans/application-audit-plans.dto";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UnitType } from "../../services/general-units/units.dto";
import { useEffect, useState } from "react";

interface AddOrUpdateAuditPlanModalProps {
  isVisible: boolean;
  onClose: () => void;
  selectedAuditPlan: UpdateApplicationAuditPlanDto | null;
  applicationId: number;
  sectorName: string;
}

const AddOrUpdateAuditPlanModal = ({
  isVisible,
  onClose,
  selectedAuditPlan,
  applicationId,
  sectorName,
}: AddOrUpdateAuditPlanModalProps) => {
  const { t } = useTranslation();
  const toaster = useAxiosContext();
  const queryClient = useQueryClient();
  const [selectedUnit, setSelectedUnit] = useState<ApplicationUnitDropdownDto | null>(null);

  const { data: applicationUnitsResponse, isLoading } = useQuery({
    queryKey: ["applicationUnits", applicationId],
    queryFn: () => applicationAuditPlanService.GetUnitsForApplication(applicationId),
    enabled: !!applicationId,
  });

  const applicationUnits = applicationUnitsResponse?.result?.units || [];

  const formik = useFormik<UpdateApplicationAuditPlanDto>({
    initialValues: {
      id: selectedAuditPlan?.id || 0,
      applicationId: applicationId,
      agricultureUnitId: selectedAuditPlan?.agricultureUnitId || null,
      generalUnitId: selectedAuditPlan?.generalUnitId || null,
      greenChemistryUnitId: selectedAuditPlan?.greenChemistryUnitId || null,
      subject: selectedAuditPlan?.subject || "",
      auditPlanDate: selectedAuditPlan?.auditPlanDate
        ? new Date(selectedAuditPlan.auditPlanDate)
        : null,
      comment: selectedAuditPlan?.comment || "",
    },
    validate: values => {
      const errors: FormikErrors<UpdateApplicationAuditPlanDto> = {};
      if (sectorName === "AGRICULTURE" && !values.agricultureUnitId) {
        errors.agricultureUnitId = "Required";
      } else if (sectorName === "GREEN CHEMISTRY" && !values.greenChemistryUnitId) {
        errors.greenChemistryUnitId = "Required";
      } else if (sectorName === "GENERAL" && !values.generalUnitId) {
        errors.generalUnitId = "Required";
      }
      if (!values.subject) {
        errors.subject = "Required";
      }
      if (!values.auditPlanDate) {
        errors.auditPlanDate = "Required";
      }
      if (!values.comment) {
        errors.comment = "Required";
      }
      console.log(values);
      console.log(errors);

      return errors;
    },
    onSubmit: values => {
      const createAuditPlanDto: CreateApplicationAuditPlanDto = {
        applicationId: applicationId,
        agricultureUnitId: values.agricultureUnitId,
        greenChemistryUnitId: values.greenChemistryUnitId,
        generalUnitId: values.generalUnitId,
        subject: values.subject,
        auditPlanDate: values.auditPlanDate,
        comment: values.comment,
      };
      if (selectedAuditPlan === null) {
        applicationAuditPlanService.Add(createAuditPlanDto, { blockUI: true }).then(response => {
          if (response.result) {
            toaster.showMessage(t("AuditPlanAdded"), "success");
            queryClient.invalidateQueries({ queryKey: ["applicationAuditPlans"] });
            onClose();
          }
        });
      } else {
        applicationAuditPlanService.Update(values, { blockUI: true }).then(response => {
          if (response.result) {
            toaster.showMessage(t("AuditPlanUpdated"), "success");
            queryClient.invalidateQueries({ queryKey: ["applicationAuditPlans"] });
            onClose();
          }
        });
      }
    },
  });

  useEffect(() => {
    if (selectedAuditPlan) {
      setSelectedUnit(
        applicationUnits.find(
          unit =>
            unit.id === selectedAuditPlan.agricultureUnitId ||
            unit.id === selectedAuditPlan.generalUnitId ||
            unit.id === selectedAuditPlan.greenChemistryUnitId
        ) || null
      );
    }
  }, [selectedAuditPlan, applicationUnits]);

  return (
    <Dialog
      draggable={false}
      header={
        <>
          <h2 className="text-primary text-xl font-semibold mt-5">
            {selectedAuditPlan === null ? t("AddAuditPlan") : t("EditAuditPlan")}
          </h2>
          <hr className="hr-color" />
        </>
      }
      footer={
        <div className="flex flex-row gap-3 justify-content-center md:justify-content-end">
          <Button
            label={t("Cancel")}
            className="justify-content-center w-10rem"
            type="button"
            outlined
            onClick={onClose}
          />
          <Button
            label={t("Save")}
            className="justify-content-center w-10rem"
            type="submit"
            onClick={() => formik.submitForm()}
          />
        </div>
      }
      visible={isVisible}
      onHide={onClose}
      className="small-dialog-width"
    >
      <form onSubmit={formik.handleSubmit} className="flex flex-column gap-2">
        <div className="flex flex-column">
          {FormLabel(
            formik,
            sectorName === "AGRICULTURE"
              ? "agricultureUnitId"
              : sectorName === "GREEN CHEMISTRY"
                ? "greenChemistryUnitId"
                : "generalUnitId",
            t("Unit")
          )}
          <div className="grid" style={{ maxHeight: "400px", overflowY: "auto" }}>
            {!isLoading && applicationUnits.length > 0 ? (
              applicationUnits.map((unit: ApplicationUnitDropdownDto) => (
                <div key={unit.id} className="col-12 md:col-6">
                  <div
                    className={`p-3 border-round cursor-pointer transition-duration-200 ${
                      selectedUnit?.id === unit.id
                        ? "surface-card border-2 border-primary shadow-2"
                        : "surface-card border-2 surface-border hover:surface-hover"
                    }`}
                    onClick={() => {
                      setSelectedUnit(unit);
                      formik.setFieldValue(
                        sectorName === "AGRICULTURE"
                          ? "agricultureUnitId"
                          : sectorName === "GREEN CHEMISTRY"
                            ? "greenChemistryUnitId"
                            : "generalUnitId",
                        unit.id
                      );
                    }}
                  >
                    <div className="flex flex-column gap-2">
                      <div className="flex align-items-center justify-content-between">
                        <span className="font-semibold text-lg">
                          {unit.id} - {unit.name}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 border-round ${
                            unit.type === UnitType.Facility
                              ? "bg-blue-100 text-blue-900"
                              : unit.type === UnitType.Subcontractor
                                ? "bg-orange-100 text-orange-900"
                                : "bg-green-100 text-green-900"
                          }`}
                        >
                          {unit.type === UnitType.Facility
                            ? t("Facility")
                            : unit.type === UnitType.Subcontractor
                              ? t("Subcontractor")
                              : t("MainUnit")}
                        </span>
                      </div>
                      <div className="text-sm text-600">
                        <div>
                          <i className="pi pi-map-marker mr-2"></i>
                          {unit.district && `${unit.district}, `}
                          {unit.province}
                        </div>
                        <div className="mt-1">
                          <i className="pi pi-globe mr-2"></i>
                          {unit.countryName}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-column gap-3 justify-content-center align-items-center h-full w-full h-5rem">
                <i className="pi pi-spinner pi-spin"></i>
                <span className="text-sm text-600">{t("LoadingUnits")}</span>
              </div>
            )}
          </div>
          {FieldErrorMessage(
            formik,
            sectorName === "AGRICULTURE"
              ? "agricultureUnitId"
              : sectorName === "GREEN CHEMISTRY"
                ? "greenChemistryUnitId"
                : "generalUnitId"
          )}
        </div>
        <div className="flex flex-column">
          {FormLabel(formik, "auditPlanDate", t("AuditPlanDate"))}
          <Calendar
            value={formik.values.auditPlanDate}
            onChange={e => formik.setFieldValue("auditPlanDate", e.value)}
            placeholder={t("SelectDate")}
            locale={i18n.language}
            dateFormat="dd/mm/yy"
            showTime
            hourFormat="24"
          />
          {FieldErrorMessage(formik, "auditPlanDate")}
        </div>
        <div className="flex flex-column">
          {FormLabel(formik, "subject", t("Subject"))}
          <InputText
            value={formik.values.subject}
            onChange={e => formik.setFieldValue("subject", e.target.value)}
            placeholder={t("EnterSubject")}
          />
          {FieldErrorMessage(formik, "subject")}
        </div>

        <div className="flex flex-column">
          {FormLabel(formik, "comment", t("Comment"))}
          <InputTextarea
            value={formik.values.comment}
            onChange={e => formik.setFieldValue("comment", e.target.value)}
            rows={5}
            cols={30}
            placeholder={t("EnterComment")}
            autoResize
          />
          {FieldErrorMessage(formik, "comment")}
        </div>
      </form>
    </Dialog>
  );
};

export default AddOrUpdateAuditPlanModal;
